<template>
  <v-btn
    :class="customCoreClass"
    variant="flat"
    :ripple="false"
    :prepend-icon="prependIcon"
    :append-icon="appendIcon"
    @click="emitClick"
  >
    <slot />
    {{ label }}
  </v-btn>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { VBtn } from 'vuetify/components'
import { ButtonSize, ButtonColor } from '../../types'

const emits = defineEmits(['click'])
const emitClick = () => emits('click')

const props = withDefaults(
  defineProps<{
    label?: string
    size?: ButtonSize
    color?: ButtonColor
    prependIcon?: string
    appendIcon?: string
  }>(),
  {
    label: '',
    size: ButtonSize.MEDIUM,
    color: ButtonColor.PRIMARY,
    prependIcon: '',
    appendIcon: ''
  }
)

const customCoreClass = computed(() => [
  'coco-button',
  `coco-button--${props.size}`,
  `coco-button--${props.color}`
])
</script>
<style lang="scss" scoped>
@import '../../styles/variables.scss';
/* Directly applying what was previously achieved using @include button-base; */
.coco-button {
  min-height: 46px;
  min-width: 100px;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 14px;
}

/* Manually applying styles for each size, previously done with @include button-size($size); */
.coco-button--small {
  min-height: 40px;
  min-width: 80px;
  padding: 12px 16px;
}

.coco-button--large {
  min-height: 54px;
  min-width: 120px;
  padding: 18px 30px;
}

.coco-button--x-large {
  height: 64px;
  min-width: 140px;
  padding: 25px 32px;
}

/* Manually applying styles for each color, previously done with @include button-color($color); */
.coco-button--primary {
  color: $text-quaternary !important;
  background-color: $background-primary;
  &:hover {
    background-color: $grey-dark;
  }
}

.coco-button--secondary {
  color: $text-primary;
  background-color: $background-senary;
  &:hover,
  &:active {
    background-color: $background-quaternary;
  }
}

.coco-button--tertiary {
  color: $text-primary !important;
  background-color: $background-octonary;
  border: 1px solid $background-senary;
}
</style>
